<script setup>
import { $api, getApiBaseUrl } from "@/utils/api";

definePage({
  meta: {
    requiresAuth: true,
  },
});

const route = useRoute();
const projetoId = computed(() => route.params.id);

const projeto = ref(null);
const loading = ref(true);
const erro = ref("");

// Seletor de datas disponíveis
const datasDisponiveis = ref([]);
const dataSelecionada = ref("");

// Dados do RDO filtrados por data
const condicoesClimaticas = ref([]);
const jornadas = ref([]);
const servicos = ref([]);
const equipamentos = ref([]);
const ocorrencias = ref([]);
const anexos = ref([]);
const assinaturas = ref({
  gerente: null,
  encarregado: null,
  planejamento: null,
});

// Listas para selects
const funcionariosOptions = ref([]);
const maquinasOptions = ref([]);

// Horários pré-setados
const horariosPadrao = {
  entrada_manha: "07:00",
  saida_manha: "11:00",
  entrada_tarde: "13:00",
  saida_tarde: "17:00",
};

// Dialogs
const dialogCondicao = ref(false);
const dialogJornada = ref(false);
const dialogServico = ref(false);
const dialogEquipamento = ref(false);
const dialogOcorrencia = ref(false);
const dialogAnexo = ref(false);
const dialogAssinatura = ref(false);
const dialogEditarProjeto = ref(false);
const dialogConfirmar = ref(false);
const confirmarMsg = ref("");
let confirmarCallback = null;
const dialogAviso = ref(false);
const avisoMsg = ref("");
const dialogConfirmarExcluirRDO = ref(null);

// Forms
const formCondicao = ref({
  id: null,
  condicao: "PRATICAVEL",
  tempo: "CLARO",
  periodo: "MANHA",
  data: "",
});
const formJornada = ref({
  id: null,
  funcionario_id: null,
  funcionario_nome: "",
  funcao: "",
  data: "",
  entrada_manha: horariosPadrao.entrada_manha,
  saida_manha: horariosPadrao.saida_manha,
  entrada_tarde: horariosPadrao.entrada_tarde,
  saida_tarde: horariosPadrao.saida_tarde,
});
const formServico = ref({
  id: null,
  frente_trabalho: "",
  descricao: "",
  funcionarios_ids: [],
  data_inicio: "",
  data_fim: "",
  somente_este_dia: false,
});
const formEquipamento = ref({
  id: null,
  maquina_id: null,
  quantidade: 1,
  operador_id: null,
  data_inicio: "",
  data_fim: "",
  somente_este_dia: false,
});
const formOcorrencia = ref({
  id: null,
  descricao: "",
  data: "",
});
const formAssinatura = ref({
  tipo: "",
  assinatura: "",
});
const formProjeto = ref({
  numero_projeto: "",
  nome_projeto: "",
  cliente_id: null,
  data_inicial: "",
  data_final: "",
  previsao_termino: "",
  prazo_final: "",
  local_cidade: "",
  dados_contratuais: "",
});

const enviando = ref(false);
const formErro = ref("");
const clientesOptions = ref([]);

const condicaoOptions = [
  { title: "Praticável", value: "PRATICAVEL" },
  { title: "Impraticável", value: "IMPRATICAVEL" },
];
const tempoOptions = [
  { title: "Claro", value: "CLARO" },
  { title: "Nublado", value: "NUBLADO" },
  { title: "Chuvoso", value: "CHUVOSO" },
];
const periodoOptions = [
  { title: "Manhã", value: "MANHA" },
  { title: "Tarde", value: "TARDE" },
];

async function fetchProjeto() {
  loading.value = true;
  erro.value = "";
  try {
    const data = await $api(`/rdo/${projetoId.value}`);
    projeto.value = data.projeto;
    datasDisponiveis.value = data.datas_disponiveis || [];
    funcionariosOptions.value = data.funcionarios || [];
    maquinasOptions.value = data.maquinas || [];

    if (datasDisponiveis.value.length > 0 && !dataSelecionada.value) {
      dataSelecionada.value = datasDisponiveis.value[0];
    }
  } catch (e) {
    console.error(e);
    erro.value = "Erro ao carregar dados do projeto.";
  } finally {
    loading.value = false;
  }
}

async function fetchDadosDia() {
  if (!dataSelecionada.value) return;
  try {
    const data = await $api(
      `/rdo/${projetoId.value}/dia?data=${dataSelecionada.value}`,
    );
    condicoesClimaticas.value = data.condicoes_climaticas || [];
    jornadas.value = data.jornadas || [];
    servicos.value = data.servicos || [];
    equipamentos.value = data.equipamentos || [];
    ocorrencias.value = data.ocorrencias || [];
    anexos.value = data.anexos || [];
    assinaturas.value = data.assinaturas || {
      gerente: null,
      encarregado: null,
      planejamento: null,
    };
  } catch (e) {
    console.error(e);
  }
}

watch(dataSelecionada, () => {
  fetchDadosDia();
});

// ========== CONDIÇÕES CLIMÁTICAS ==========
function abrirDialogCondicao(item = null) {
  if (item) {
    formCondicao.value = { ...item };
  } else {
    formCondicao.value = {
      id: null,
      condicao: "PRATICAVEL",
      tempo: "CLARO",
      periodo: "MANHA",
      data: dataSelecionada.value || "",
    };
  }
  formErro.value = "";
  dialogCondicao.value = true;
}

async function salvarCondicao() {
  if (!formCondicao.value.data) {
    formErro.value = "Informe a data.";
    return;
  }
  enviando.value = true;
  formErro.value = "";
  try {
    const method = formCondicao.value.id ? "PUT" : "POST";
    const url = formCondicao.value.id
      ? `/rdo/${projetoId.value}/condicoes/${formCondicao.value.id}`
      : `/rdo/${projetoId.value}/condicoes`;
    await $api(url, { method, body: formCondicao.value });
    dialogCondicao.value = false;
    atualizarDatasDisponiveis(formCondicao.value.data);
    fetchDadosDia();
  } catch (e) {
    formErro.value = e?.data?.message || "Erro ao salvar.";
  } finally {
    enviando.value = false;
  }
}

function abrirConfirmar(msg, callback) {
  confirmarMsg.value = msg;
  confirmarCallback = callback;
  dialogConfirmar.value = true;
}

async function executarConfirmar(param = false) {
  const cb = confirmarCallback;
  const isSpecial = !!dialogConfirmarExcluirRDO.value;
  fecharConfirmar();
  if (cb) {
    if (isSpecial) await cb(param);
    else await cb();
  }
}

function fecharConfirmar() {
  dialogConfirmar.value = false;
  confirmarMsg.value = "";
  confirmarCallback = null;
  dialogConfirmarExcluirRDO.value = null;
}

function mostrarAviso(msg) {
  avisoMsg.value = msg;
  dialogAviso.value = true;
}



function fecharAviso() {
  dialogAviso.value = false;
  avisoMsg.value = "";
}

async function excluirCondicao(id) {
  abrirConfirmar("Deseja excluir esta condição climática?", async () => {
    try {
      await $api(`/rdo/${projetoId.value}/condicoes/${id}`, { method: "DELETE" });
      fetchDadosDia();
    } catch (e) {
      console.error(e);
      mostrarAviso(e?.data?.message || "Erro ao excluir.");
    }
  });
}

// ========== JORNADAS ==========
function abrirDialogJornada(item = null) {
  if (item) {
    formJornada.value = { ...item };
  } else {
    formJornada.value = {
      id: null,
      funcionario_id: null,
      funcionario_nome: "",
      funcao: "",
      data: dataSelecionada.value || "",
      entrada_manha: horariosPadrao.entrada_manha,
      saida_manha: horariosPadrao.saida_manha,
      entrada_tarde: horariosPadrao.entrada_tarde,
      saida_tarde: horariosPadrao.saida_tarde,
    };
  }
  formErro.value = "";
  dialogJornada.value = true;
}

function onFuncionarioChange(funcId) {
  const func = funcionariosOptions.value.find((f) => f.id === funcId);
  if (func) {
    formJornada.value.funcionario_nome = func.nome;
    formJornada.value.funcao = func.funcao || "";
  }
}

async function salvarJornada() {
  if (!formJornada.value.funcionario_id) {
    formErro.value = "Selecione o funcionário.";
    return;
  }
  if (!formJornada.value.data) {
    formErro.value = "Informe a data.";
    return;
  }
  enviando.value = true;
  formErro.value = "";
  try {
    const method = formJornada.value.id ? "PUT" : "POST";
    const url = formJornada.value.id
      ? `/rdo/${projetoId.value}/jornadas/${formJornada.value.id}`
      : `/rdo/${projetoId.value}/jornadas`;
    await $api(url, { method, body: formJornada.value });
    dialogJornada.value = false;
    atualizarDatasDisponiveis(formJornada.value.data);
    fetchDadosDia();
  } catch (e) {
    formErro.value = e?.data?.message || "Erro ao salvar.";
  } finally {
    enviando.value = false;
  }
}

async function excluirJornada(id) {
  abrirConfirmar("Deseja excluir esta jornada?", async () => {
    try {
      await $api(`/rdo/${projetoId.value}/jornadas/${id}`, { method: "DELETE" });
      fetchDadosDia();
    } catch (e) {
      console.error(e);
      mostrarAviso(e?.data?.message || "Erro ao excluir.");
    }
  });
}

// ========== SERVIÇOS ==========
function abrirDialogServico(item = null) {
  if (item) {
    formServico.value = {
      id: item.id,
      frente_trabalho: item.frente_trabalho || "",
      descricao: item.descricao || "",
      funcionarios_ids: item.funcionarios?.map((f) => f.id) || [],
      data_inicio: item.data_inicio || "",
      data_fim: item.data_fim || "",
      somente_este_dia: false,
    };
  } else {
    formServico.value = {
      id: null,
      frente_trabalho: "",
      descricao: "",
      funcionarios_ids: [],
      data_inicio: "",
      data_fim: "",
      somente_este_dia: false,
    };
  }
  formErro.value = "";
  dialogServico.value = true;
}

// Funcionários do dia atual (que têm jornada nessa data)
const funcionariosDoDia = computed(() => {
  return jornadas.value.map((j) => ({
    id: j.funcionario_id,
    nome: j.funcionario_nome,
  }));
});

async function salvarServico() {
  if (!formServico.value.frente_trabalho?.trim()) {
    formErro.value = "Informe a frente de trabalho.";
    return;
  }
  if (!formServico.value.descricao?.trim()) {
    formErro.value = "Informe a descrição do serviço.";
    return;
  }
  enviando.value = true;
  formErro.value = "";
  try {
    const method = formServico.value.id ? "PUT" : "POST";
    const url = formServico.value.id
      ? `/rdo/${projetoId.value}/servicos/${formServico.value.id}`
      : `/rdo/${projetoId.value}/servicos`;
    
    const payload = {
      ...formServico.value,
      data_referencia: dataSelecionada.value,
    };

    await $api(url, { method, body: payload });
    dialogServico.value = false;
    atualizarDatasDisponiveis(formServico.value.data_inicio || dataSelecionada.value);
    fetchDadosDia();
  } catch (e) {
    formErro.value = e?.data?.message || "Erro ao salvar.";
  } finally {
    enviando.value = false;
  }
}

async function excluirServico(id) {
  confirmarMsg.value = "Deseja excluir este serviço?";
  confirmarCallback = async (somenteHoje = false) => {
    try {
      await $api(`/rdo/${projetoId.value}/servicos/${id}`, { 
        method: "DELETE",
        params: { 
          somente_este_dia: somenteHoje ? 1 : 0,
          data_referencia: dataSelecionada.value
        }
      });
      fetchDadosDia();
    } catch (e) {
      console.error(e);
      mostrarAviso(e?.data?.message || "Erro ao excluir.");
    }
  };
  dialogConfirmarExcluirRDO.value = { type: 'servico', id };
  dialogConfirmar.value = true;
}

// ========== EQUIPAMENTOS ==========
function abrirDialogEquipamento(item = null) {
  if (item) {
    formEquipamento.value = { 
      id: item.id,
      maquina_id: item.maquina_id,
      quantidade: item.quantidade,
      operador_id: item.operador_id,
      data_inicio: item.data_inicio || "",
      data_fim: item.data_fim || "",
      somente_este_dia: false,
    };
  } else {
    formEquipamento.value = {
      id: null,
      maquina_id: null,
      quantidade: 1,
      operador_id: null,
      data_inicio: "",
      data_fim: "",
      somente_este_dia: false,
    };
  }
  formErro.value = "";
  dialogEquipamento.value = true;
}

async function salvarEquipamento() {
  if (!formEquipamento.value.maquina_id) {
    formErro.value = "Selecione a máquina.";
    return;
  }
  if (!formEquipamento.value.operador_id) {
    formErro.value = "Selecione o operador.";
    return;
  }
  enviando.value = true;
  formErro.value = "";
  try {
    const method = formEquipamento.value.id ? "PUT" : "POST";
    const url = formEquipamento.value.id
      ? `/rdo/${projetoId.value}/equipamentos/${formEquipamento.value.id}`
      : `/rdo/${projetoId.value}/equipamentos`;
    
    const payload = {
      ...formEquipamento.value,
      data_referencia: dataSelecionada.value,
    };

    await $api(url, { method, body: payload });
    dialogEquipamento.value = false;
    atualizarDatasDisponiveis(formEquipamento.value.data_inicio || dataSelecionada.value);
    fetchDadosDia();
  } catch (e) {
    formErro.value = e?.data?.message || "Erro ao salvar.";
  } finally {
    enviando.value = false;
  }
}

async function excluirEquipamento(id) {
  confirmarMsg.value = "Deseja excluir este equipamento?";
  confirmarCallback = async (somenteHoje = false) => {
    try {
      await $api(`/rdo/${projetoId.value}/equipamentos/${id}`, { 
        method: "DELETE",
        params: { 
          somente_este_dia: somenteHoje ? 1 : 0,
          data_referencia: dataSelecionada.value
        }
      });
      fetchDadosDia();
    } catch (e) {
      console.error(e);
      mostrarAviso(e?.data?.message || "Erro ao excluir.");
    }
  };
  dialogConfirmarExcluirRDO.value = { type: 'equipamento', id };
  dialogConfirmar.value = true;
}

// ========== OCORRÊNCIAS ==========
function abrirDialogOcorrencia(item = null) {
  if (item) {
    formOcorrencia.value = { ...item };
  } else {
    formOcorrencia.value = {
      id: null,
      descricao: "",
      data: dataSelecionada.value || "",
    };
  }
  formErro.value = "";
  dialogOcorrencia.value = true;
}

async function salvarOcorrencia() {
  if (!formOcorrencia.value.descricao?.trim()) {
    formErro.value = "Informe a descrição da ocorrência.";
    return;
  }
  if (!formOcorrencia.value.data) {
    formErro.value = "Informe a data.";
    return;
  }
  enviando.value = true;
  formErro.value = "";
  try {
    const method = formOcorrencia.value.id ? "PUT" : "POST";
    const url = formOcorrencia.value.id
      ? `/rdo/${projetoId.value}/ocorrencias/${formOcorrencia.value.id}`
      : `/rdo/${projetoId.value}/ocorrencias`;
    await $api(url, { method, body: formOcorrencia.value });
    dialogOcorrencia.value = false;
    atualizarDatasDisponiveis(formOcorrencia.value.data);
    fetchDadosDia();
  } catch (e) {
    formErro.value = e?.data?.message || "Erro ao salvar.";
  } finally {
    enviando.value = false;
  }
}

async function excluirOcorrencia(id) {
  abrirConfirmar("Deseja excluir esta ocorrência?", async () => {
    try {
      await $api(`/rdo/${projetoId.value}/ocorrencias/${id}`, { method: "DELETE" });
      fetchDadosDia();
    } catch (e) {
      console.error(e);
      mostrarAviso(e?.data?.message || "Erro ao excluir.");
    }
  });
}

// ========== ANEXOS ==========
const anexoFile = ref(null);

function getAnexoFiles() {
  const v = anexoFile.value;
  if (!v) return [];
  if (Array.isArray(v)) return v.filter((f) => f instanceof File);
  if (v instanceof File) return [v];
  return [];
}

async function uploadAnexo() {
  const files = getAnexoFiles();
  if (!files.length) return;

  const token = useCookie("accessToken").value;
  const apiBase = getApiBaseUrl();
  enviando.value = true;
  try {
    for (const file of files) {
      const fd = new FormData();
      fd.append("arquivo", file);
      fd.append("data", dataSelecionada.value);
      await fetch(`${apiBase}/rdo/${projetoId.value}/anexos`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
    }
    anexoFile.value = null;
    atualizarDatasDisponiveis(dataSelecionada.value);
    fetchDadosDia();
  } catch (e) {
    console.error(e);
  } finally {
    enviando.value = false;
  }
}

async function excluirAnexo(id) {
  confirmarMsg.value = "Deseja excluir este anexo?";
  confirmarCallback = async (somenteHoje = false) => {
    try {
      await $api(`/rdo/${projetoId.value}/anexos/${id}`, { 
        method: "DELETE",
        params: { 
          somente_este_dia: somenteHoje ? 1 : 0,
          data_referencia: dataSelecionada.value
        }
      });
      fetchDadosDia();
    } catch (e) {
      console.error(e);
      mostrarAviso(e?.data?.message || "Erro ao excluir.");
    }
  };
  dialogConfirmarExcluirRDO.value = { type: 'anexo', id };
  dialogConfirmar.value = true;
}

// ========== ASSINATURAS COM CANVAS ==========
const canvasAssinatura = ref(null);
let ctx = null;
let desenhando = false;
let ultimoX = 0;
let ultimoY = 0;

const tipoAssinaturaLabel = computed(() => {
  const labels = {
    gerente: "Gerente",
    encarregado: "Encarregado",
    planejamento: "Planejamento",
  };
  return labels[formAssinatura.value.tipo] || formAssinatura.value.tipo;
});

function abrirDialogAssinatura(tipo) {
  formAssinatura.value = {
    tipo,
    assinatura: "",
  };
  formErro.value = "";
  dialogAssinatura.value = true;
  nextTick(() => {
    inicializarCanvas();
  });
}

function fecharDialogAssinatura() {
  dialogAssinatura.value = false;
  desenhando = false;
}

function inicializarCanvas() {
  const canvas = canvasAssinatura.value;
  if (!canvas) return;
  ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.strokeStyle = "#000000";
  ctx.lineWidth = 2;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
}

function limparCanvas() {
  const canvas = canvasAssinatura.value;
  if (!canvas || !ctx) return;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
}

function getCanvasCoords(e) {
  const canvas = canvasAssinatura.value;
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;
  return {
    x: (e.clientX - rect.left) * scaleX,
    y: (e.clientY - rect.top) * scaleY,
  };
}

function iniciarDesenho(e) {
  desenhando = true;
  const coords = getCanvasCoords(e);
  ultimoX = coords.x;
  ultimoY = coords.y;
}

function desenhar(e) {
  if (!desenhando || !ctx) return;
  const coords = getCanvasCoords(e);
  ctx.beginPath();
  ctx.moveTo(ultimoX, ultimoY);
  ctx.lineTo(coords.x, coords.y);
  ctx.stroke();
  ultimoX = coords.x;
  ultimoY = coords.y;
}

function pararDesenho() {
  desenhando = false;
}

function iniciarDesenhoTouch(e) {
  if (e.touches.length === 1) {
    const touch = e.touches[0];
    const canvas = canvasAssinatura.value;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    ultimoX = (touch.clientX - rect.left) * scaleX;
    ultimoY = (touch.clientY - rect.top) * scaleY;
    desenhando = true;
  }
}

function desenharTouch(e) {
  if (!desenhando || !ctx || e.touches.length !== 1) return;
  const touch = e.touches[0];
  const canvas = canvasAssinatura.value;
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;
  const x = (touch.clientX - rect.left) * scaleX;
  const y = (touch.clientY - rect.top) * scaleY;
  ctx.beginPath();
  ctx.moveTo(ultimoX, ultimoY);
  ctx.lineTo(x, y);
  ctx.stroke();
  ultimoX = x;
  ultimoY = y;
}

function canvasVazio() {
  const canvas = canvasAssinatura.value;
  if (!canvas) return true;
  const ctx2 = canvas.getContext("2d");
  const imageData = ctx2.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    if (data[i] !== 255 || data[i + 1] !== 255 || data[i + 2] !== 255) {
      return false;
    }
  }
  return true;
}

async function salvarAssinatura() {
  if (canvasVazio()) {
    formErro.value = "Por favor, desenhe sua assinatura.";
    return;
  }
  if (!formAssinatura.value.tipo) {
    formErro.value = "Tipo de assinatura não definido.";
    return;
  }

  let dataParaAssinatura = dataSelecionada.value;
  if (!dataParaAssinatura) {
    dataParaAssinatura = new Date().toISOString().slice(0, 10);
    dataSelecionada.value = dataParaAssinatura;
    if (!datasDisponiveis.value.includes(dataParaAssinatura)) {
      datasDisponiveis.value = [...datasDisponiveis.value, dataParaAssinatura].sort();
    }
  }

  const canvas = canvasAssinatura.value;
  const assinaturaBase64 = canvas.toDataURL("image/png");

  enviando.value = true;
  formErro.value = "";
  try {
    const body = {
      tipo: formAssinatura.value.tipo,
      assinatura: assinaturaBase64,
      data: dataParaAssinatura,
    };
    const res = await $api(`/rdo/${projetoId.value}/assinaturas`, {
      method: "POST",
      body,
      headers: { "Content-Type": "application/json" },
    });
    dialogAssinatura.value = false;
    if (res?.assinatura) {
      assinaturas.value = { ...assinaturas.value, [res.assinatura.tipo]: res.assinatura.assinatura };
    } else {
      await fetchDadosDia();
    }
  } catch (e) {
    formErro.value = e?.data?.message || e?.message || "Erro ao salvar.";
  } finally {
    enviando.value = false;
  }
}

async function limparAssinatura(tipo) {
  abrirConfirmar("Deseja limpar esta assinatura?", async () => {
    try {
      await $api(
        `/rdo/${projetoId.value}/assinaturas/${tipo}?data=${dataSelecionada.value}`,
        { method: "DELETE" },
      );
      fetchDadosDia();
    } catch (e) {
      console.error(e);
      mostrarAviso(e?.data?.message || "Erro ao limpar.");
    }
  });
}

// ========== EDITAR PROJETO ==========
async function abrirDialogEditarProjeto() {
  formProjeto.value = {
    numero_projeto: projeto.value?.numero_projeto || "",
    nome_projeto: projeto.value?.nome || "",
    cliente_id: projeto.value?.cliente_id || null,
    data_inicial: projeto.value?.data_inicial || "",
    data_final: projeto.value?.data_final || "",
    previsao_termino: projeto.value?.previsao_termino || "",
    prazo_final: projeto.value?.prazo_final || "",
    local_cidade: projeto.value?.local_cidade || "",
    dados_contratuais: projeto.value?.dados_contratuais || "",
  };
  formErro.value = "";
  dialogEditarProjeto.value = true;

  try {
    const data = await $api("/clientes");
    clientesOptions.value = Array.isArray(data) ? data : data.data ?? [];
  } catch (e) {
    clientesOptions.value = [];
  }
}

async function salvarProjeto() {
  enviando.value = true;
  formErro.value = "";
  try {
    await $api(`/projetos/${projetoId.value}`, {
      method: "PUT",
      body: formProjeto.value,
    });
    dialogEditarProjeto.value = false;
    fetchProjeto();
  } catch (e) {
    formErro.value = e?.data?.message || "Erro ao salvar.";
  } finally {
    enviando.value = false;
  }
}

function atualizarDatasDisponiveis(novaData) {
  if (novaData && !datasDisponiveis.value.includes(novaData)) {
    datasDisponiveis.value.push(novaData);
    datasDisponiveis.value.sort();
  }
  if (!dataSelecionada.value) {
    dataSelecionada.value = novaData;
  }
}

function formatarData(data) {
  if (!data) return "—";
  const [ano, mes, dia] = data.split("-");
  return new Date(ano, mes - 1, dia).toLocaleDateString("pt-BR");
}

function imprimirRdo() {
  const dataRelatorio =
    dataSelecionada.value ||
    new Date().toISOString().split("T")[0];
  const apiBase = getApiBaseUrl();
  const url = `${apiBase}/rdo/${projetoId.value}/pdf?data=${encodeURIComponent(dataRelatorio)}`;
  window.open(url, "_blank");
}

onMounted(() => {
  fetchProjeto();
});
</script>

<template>
  <div>
    <VRow>
      <VCol cols="12">
        <!-- Header -->
        <VCard class="mb-4 sticky-header">
          <VCardTitle class="d-flex align-center pa-4 flex-wrap">
            <div class="d-flex align-center">
              <VBtn
                icon
                variant="text"
                size="small"
                :to="{ name: 'rdo' }"
                class="me-2">
                <VIcon icon="tabler-arrow-left" />
              </VBtn>
              <div>
                <div class="text-h5">RDO - Diário de Obras</div>
                <div class="text-caption text-medium-emphasis d-none d-sm-block">Relatório Diário de Obras</div>
              </div>
            </div>

            <VSpacer />

            <div class="d-flex align-center gap-4">
              <div v-if="dataSelecionada" class="d-none d-md-flex flex-column align-end me-4">
                <span class="text-overline line-height-1 text-primary" style="font-size: 0.7rem !important; opacity: 0.8;">Data do Relatório</span>
                <span class="text-h6 font-weight-bold text-primary">{{ formatarData(dataSelecionada) }}</span>
              </div>

              <VSelect
                v-if="datasDisponiveis.length > 0"
                v-model="dataSelecionada"
                :items="
                  datasDisponiveis.map((d) => ({
                    title: formatarData(d),
                    value: d,
                  }))
                "
                label="Selecionar Data"
                density="compact"
                variant="outlined"
                style="min-width: 160px; max-width: 200px"
                hide-details
                prepend-inner-icon="tabler-calendar-edit" />
            </div>
          </VCardTitle>
        </VCard>

        <VAlert v-if="erro" type="error" class="mb-4">{{ erro }}</VAlert>
        <VProgressLinear v-if="loading" indeterminate class="mb-4" />

        <template v-if="projeto && !loading">
          <!-- Dados Contratuais -->
          <VCard class="mb-4">
            <VCardTitle class="d-flex align-center">
              <VIcon icon="tabler-file-description" class="me-2" />
              Dados Contratuais
              <VSpacer />
              <VBtn
                size="small"
                variant="text"
                @click="abrirDialogEditarProjeto"
                class="me-2">
                <VIcon icon="tabler-edit" start />
                Editar
              </VBtn>
              <VBtn
                size="small"
                color="info"
                @click="imprimirRdo">
                <VIcon icon="tabler-printer" start />
                Imprimir RDO
              </VBtn>
            </VCardTitle>
            <VDivider />
            <VCardText>
              <VRow>
                <VCol cols="12" md="6">
                  <p><strong>Número:</strong> {{ projeto.numero_projeto }}</p>
                  <p><strong>Nome:</strong> {{ projeto.nome }}</p>
                  <p>
                    <strong>Cliente:</strong> {{ projeto.cliente_nome || "—" }}
                  </p>
                </VCol>
                <VCol cols="12" md="6">
                  <p>
                    <strong>Data Inicial:</strong>
                    {{ formatarData(projeto.data_inicial) }}
                  </p>
                  <p>
                    <strong>Data Final:</strong>
                    {{ formatarData(projeto.data_final) }}
                  </p>
                  <p>
                    <strong>Local:</strong> {{ projeto.local_cidade || "—" }}
                  </p>
                </VCol>
                <VCol v-if="projeto.dados_contratuais" cols="12">
                  <p><strong>Dados Contratuais:</strong></p>
                  <p class="text-body-2">{{ projeto.dados_contratuais }}</p>
                </VCol>
              </VRow>
            </VCardText>
          </VCard>

          <!-- Condições Climáticas -->
          <VCard class="mb-4">
            <VCardTitle class="d-flex align-center">
              <VIcon icon="tabler-cloud" class="me-2" />
              Condições Climáticas
              <VSpacer />
              <VBtn size="small" color="primary" @click="abrirDialogCondicao()">
                <VIcon icon="tabler-plus" start />
                Adicionar
              </VBtn>
            </VCardTitle>
            <VDivider />
            <VCardText>
              <VTable v-if="condicoesClimaticas.length > 0" density="compact">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Período</th>
                    <th>Condição</th>
                    <th>Tempo</th>
                    <th width="100">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in condicoesClimaticas" :key="item.id">
                    <td>{{ formatarData(item.data) }}</td>
                    <td>{{ item.periodo }}</td>
                    <td>
                      <VChip
                        :color="
                          item.condicao === 'PRATICAVEL' ? 'success' : 'error'
                        "
                        size="small">
                        {{ item.condicao }}
                      </VChip>
                    </td>
                    <td>{{ item.tempo }}</td>
                    <td>
                      <VBtn
                        icon
                        variant="text"
                        size="x-small"
                        @click="abrirDialogCondicao(item)">
                        <VIcon icon="tabler-edit" />
                      </VBtn>
                      <VBtn
                        icon
                        variant="text"
                        size="x-small"
                        color="error"
                        @click="excluirCondicao(item.id)">
                        <VIcon icon="tabler-trash" />
                      </VBtn>
                    </td>
                  </tr>
                </tbody>
              </VTable>
              <p v-else class="text-body-2 text-medium-emphasis">
                Nenhuma condição climática registrada para esta data.
              </p>
            </VCardText>
          </VCard>

          <!-- Funcionários / Jornada de Trabalho -->
          <VCard class="mb-4">
            <VCardTitle class="d-flex align-center">
              <VIcon icon="tabler-users" class="me-2" />
              Funcionários / Jornada de Trabalho
              <VSpacer />
              <VBtn size="small" color="primary" @click="abrirDialogJornada()">
                <VIcon icon="tabler-plus" start />
                Adicionar
              </VBtn>
            </VCardTitle>
            <VDivider />
            <VCardText>
              <VTable v-if="jornadas.length > 0" density="compact">
                <thead>
                  <tr>
                    <th>Funcionário</th>
                    <th>Função</th>
                    <th>Data</th>
                    <th>Entrada Manhã</th>
                    <th>Saída Manhã</th>
                    <th>Entrada Tarde</th>
                    <th>Saída Tarde</th>
                    <th width="100">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in jornadas" :key="item.id">
                    <td>{{ item.funcionario_nome }}</td>
                    <td>{{ item.funcao }}</td>
                    <td>{{ formatarData(item.data) }}</td>
                    <td>{{ item.entrada_manha }}</td>
                    <td>{{ item.saida_manha }}</td>
                    <td>{{ item.entrada_tarde }}</td>
                    <td>{{ item.saida_tarde }}</td>
                    <td>
                      <VBtn
                        icon
                        variant="text"
                        size="x-small"
                        @click="abrirDialogJornada(item)">
                        <VIcon icon="tabler-edit" />
                      </VBtn>
                      <VBtn
                        icon
                        variant="text"
                        size="x-small"
                        color="error"
                        @click="excluirJornada(item.id)">
                        <VIcon icon="tabler-trash" />
                      </VBtn>
                    </td>
                  </tr>
                </tbody>
              </VTable>
              <p v-else class="text-body-2 text-medium-emphasis">
                Nenhuma jornada registrada para esta data.
              </p>
            </VCardText>
          </VCard>

          <!-- Frente de Trabalho / Serviços -->
          <VCard class="mb-4">
            <VCardTitle class="d-flex align-center">
              <VIcon icon="tabler-hammer" class="me-2" />
              Frente de Trabalho
              <VSpacer />
              <VBtn size="small" color="primary" @click="abrirDialogServico()">
                <VIcon icon="tabler-plus" start />
                Adicionar
              </VBtn>
            </VCardTitle>
            <VDivider />
            <VCardText>
              <VTable v-if="servicos.length > 0" density="compact">
                <thead>
                  <tr>
                    <th>Frente de Trabalho</th>
                    <th>Descrição do Serviço</th>
                    <th>Efetivo</th>
                    <th>Início</th>
                    <th>Término</th>
                    <th width="100">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in servicos" :key="item.id">
                    <td>{{ item.frente_trabalho || "—" }}</td>
                    <td>{{ item.descricao }}</td>
                    <td>
                      <VChip
                        v-for="func in item.funcionarios"
                        :key="func.id"
                        size="small"
                        class="me-1 mb-1">
                        {{ func.nome }}
                      </VChip>
                    </td>
                    <td>{{ formatarData(item.data_inicio) }}</td>
                    <td>{{ formatarData(item.data_fim) }}</td>
                    <td>
                      <VBtn
                        icon
                        variant="text"
                        size="x-small"
                        @click="abrirDialogServico(item)">
                        <VIcon icon="tabler-edit" />
                      </VBtn>
                      <VBtn
                        icon
                        variant="text"
                        size="x-small"
                        color="error"
                        @click="excluirServico(item.id)">
                        <VIcon icon="tabler-trash" />
                      </VBtn>
                    </td>
                  </tr>
                </tbody>
              </VTable>
              <p v-else class="text-body-2 text-medium-emphasis">
                Nenhum serviço registrado.
              </p>
            </VCardText>
          </VCard>

          <!-- Equipamentos -->
          <VCard class="mb-4">
            <VCardTitle class="d-flex align-center">
              <VIcon icon="tabler-bulldozer" class="me-2" />
              Equipamentos
              <VSpacer />
              <VBtn
                size="small"
                color="primary"
                @click="abrirDialogEquipamento()">
                <VIcon icon="tabler-plus" start />
                Adicionar
              </VBtn>
            </VCardTitle>
            <VDivider />
            <VCardText>
              <VTable v-if="equipamentos.length > 0" density="compact">
                <thead>
                  <tr>
                    <th>Máquina</th>
                    <th>Quantidade</th>
                    <th>Operador</th>
                    <th>Início</th>
                    <th>Término</th>
                    <th width="100">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in equipamentos" :key="item.id">
                    <td>{{ item.maquina_nome }}</td>
                    <td>{{ item.quantidade }}</td>
                    <td>{{ item.operador_nome }}</td>
                    <td>{{ formatarData(item.data_inicio) }}</td>
                    <td>{{ formatarData(item.data_fim) }}</td>
                    <td>
                      <VBtn
                        icon
                        variant="text"
                        size="x-small"
                        @click="abrirDialogEquipamento(item)">
                        <VIcon icon="tabler-edit" />
                      </VBtn>
                      <VBtn
                        icon
                        variant="text"
                        size="x-small"
                        color="error"
                        @click="excluirEquipamento(item.id)">
                        <VIcon icon="tabler-trash" />
                      </VBtn>
                    </td>
                  </tr>
                </tbody>
              </VTable>
              <p v-else class="text-body-2 text-medium-emphasis">
                Nenhum equipamento registrado.
              </p>
            </VCardText>
          </VCard>

          <!-- Ocorrências -->
          <VCard class="mb-4">
            <VCardTitle class="d-flex align-center">
              <VIcon icon="tabler-alert-triangle" class="me-2" />
              Ocorrências
              <VSpacer />
              <VBtn
                size="small"
                color="primary"
                @click="abrirDialogOcorrencia()">
                <VIcon icon="tabler-plus" start />
                Adicionar
              </VBtn>
            </VCardTitle>
            <VDivider />
            <VCardText>
              <VTable v-if="ocorrencias.length > 0" density="compact">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Descrição</th>
                    <th width="100">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in ocorrencias" :key="item.id">
                    <td>{{ formatarData(item.data) }}</td>
                    <td>{{ item.descricao }}</td>
                    <td>
                      <VBtn
                        icon
                        variant="text"
                        size="x-small"
                        @click="abrirDialogOcorrencia(item)">
                        <VIcon icon="tabler-edit" />
                      </VBtn>
                      <VBtn
                        icon
                        variant="text"
                        size="x-small"
                        color="error"
                        @click="excluirOcorrencia(item.id)">
                        <VIcon icon="tabler-trash" />
                      </VBtn>
                    </td>
                  </tr>
                </tbody>
              </VTable>
              <p v-else class="text-body-2 text-medium-emphasis">
                Nenhuma ocorrência registrada.
              </p>
            </VCardText>
          </VCard>

          <!-- Anexos / Relatório Fotográfico -->
          <VCard class="mb-4">
            <VCardTitle class="d-flex align-center">
              <VIcon icon="tabler-photo" class="me-2" />
              Anexos - Relatório Fotográfico
            </VCardTitle>
            <VDivider />
            <VCardText>
              <div class="d-flex align-center gap-3 mb-4 flex-wrap">
                <VFileInput
                  v-model="anexoFile"
                  label="Adicionar fotos (apenas imagens)"
                  density="compact"
                  variant="outlined"
                  prepend-icon=""
                  prepend-inner-icon="tabler-photo"
                  accept="image/*"
                  multiple
                  hide-details
                  style="max-width: 400px"
                  show-size />
                <VBtn
                  color="primary"
                  :disabled="!getAnexoFiles().length"
                  :loading="enviando"
                  @click="uploadAnexo">
                  <VIcon icon="tabler-upload" start />
                  Enviar
                  {{
                    getAnexoFiles().length ? `(${getAnexoFiles().length})` : ""
                  }}
                </VBtn>
              </div>
              <div v-if="anexos.length > 0" class="d-flex flex-wrap gap-3">
                <VCard
                  v-for="anexo in anexos"
                  :key="anexo.id"
                  width="150"
                  class="pa-2">
                  <VImg
                    v-if="anexo.tipo === 'imagem'"
                    :src="anexo.url"
                    height="100"
                    cover
                    class="rounded mb-2" />
                  <VIcon
                    v-else
                    icon="tabler-file"
                    size="64"
                    class="d-block mx-auto mb-2" />
                  <div class="d-flex justify-center">
                    <VBtn
                      icon
                      variant="text"
                      size="x-small"
                      color="error"
                      @click="excluirAnexo(anexo.id)">
                      <VIcon icon="tabler-trash" />
                    </VBtn>
                  </div>
                </VCard>
              </div>
              <p v-else class="text-body-2 text-medium-emphasis">
                Nenhum anexo enviado.
              </p>
            </VCardText>
          </VCard>

          <!-- Assinaturas -->
          <VCard class="mb-4">
            <VCardTitle class="d-flex align-center">
              <VIcon icon="tabler-signature" class="me-2" />
              Assinaturas
            </VCardTitle>
            <VDivider />
            <VCardText>
              <VRow>
                <VCol cols="12" md="4">
                  <VCard variant="outlined" class="pa-3">
                    <p class="text-subtitle-2 mb-2">
                      Gerente do Diário de Obras
                    </p>
                    <div
                      v-if="assinaturas.gerente"
                      class="assinatura-preview mb-2">
                      <img
                        :src="assinaturas.gerente"
                        alt="Assinatura Gerente"
                        class="assinatura-img" />
                    </div>
                    <p v-else class="text-body-2 text-medium-emphasis">
                      Sem assinatura
                    </p>
                    <div class="d-flex gap-1 mt-2">
                      <VBtn
                        size="x-small"
                        variant="text"
                        @click="abrirDialogAssinatura('gerente')">
                        <VIcon icon="tabler-pencil" start />
                        Assinar
                      </VBtn>
                      <VBtn
                        v-if="assinaturas.gerente"
                        size="x-small"
                        variant="text"
                        color="error"
                        @click="limparAssinatura('gerente')">
                        <VIcon icon="tabler-x" start />
                        Limpar
                      </VBtn>
                    </div>
                  </VCard>
                </VCol>
                <VCol cols="12" md="4">
                  <VCard variant="outlined" class="pa-3">
                    <p class="text-subtitle-2 mb-2">
                      Encarregado do Diário de Obras
                    </p>
                    <div
                      v-if="assinaturas.encarregado"
                      class="assinatura-preview mb-2">
                      <img
                        :src="assinaturas.encarregado"
                        alt="Assinatura Encarregado"
                        class="assinatura-img" />
                    </div>
                    <p v-else class="text-body-2 text-medium-emphasis">
                      Sem assinatura
                    </p>
                    <div class="d-flex gap-1 mt-2">
                      <VBtn
                        size="x-small"
                        variant="text"
                        @click="abrirDialogAssinatura('encarregado')">
                        <VIcon icon="tabler-pencil" start />
                        Assinar
                      </VBtn>
                      <VBtn
                        v-if="assinaturas.encarregado"
                        size="x-small"
                        variant="text"
                        color="error"
                        @click="limparAssinatura('encarregado')">
                        <VIcon icon="tabler-x" start />
                        Limpar
                      </VBtn>
                    </div>
                  </VCard>
                </VCol>
                <VCol cols="12" md="4">
                  <VCard variant="outlined" class="pa-3">
                    <p class="text-subtitle-2 mb-2">
                      Planejamento do Diário de Obras
                    </p>
                    <div
                      v-if="assinaturas.planejamento"
                      class="assinatura-preview mb-2">
                      <img
                        :src="assinaturas.planejamento"
                        alt="Assinatura Planejamento"
                        class="assinatura-img" />
                    </div>
                    <p v-else class="text-body-2 text-medium-emphasis">
                      Sem assinatura
                    </p>
                    <div class="d-flex gap-1 mt-2">
                      <VBtn
                        size="x-small"
                        variant="text"
                        @click="abrirDialogAssinatura('planejamento')">
                        <VIcon icon="tabler-pencil" start />
                        Assinar
                      </VBtn>
                      <VBtn
                        v-if="assinaturas.planejamento"
                        size="x-small"
                        variant="text"
                        color="error"
                        @click="limparAssinatura('planejamento')">
                        <VIcon icon="tabler-x" start />
                        Limpar
                      </VBtn>
                    </div>
                  </VCard>
                </VCol>
              </VRow>
            </VCardText>
          </VCard>
        </template>
      </VCol>
    </VRow>

    <!-- Dialog Condição Climática -->
    <VDialog v-model="dialogCondicao" max-width="500" persistent>
      <VCard>
        <VCardTitle>
          <VIcon icon="tabler-cloud" class="me-2" />
          {{ formCondicao.id ? "Editar" : "Nova" }} Condição Climática
        </VCardTitle>
        <VDivider />
        <VCardText>
          <VForm @submit.prevent="salvarCondicao">
            <VRow>
              <VCol cols="12" md="6">
                <VSelect
                  v-model="formCondicao.condicao"
                  :items="condicaoOptions"
                  label="Condição *"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="12" md="6">
                <VSelect
                  v-model="formCondicao.tempo"
                  :items="tempoOptions"
                  label="Tempo *"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="12" md="6">
                <VSelect
                  v-model="formCondicao.periodo"
                  :items="periodoOptions"
                  label="Período *"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="12" md="6">
                <VTextField
                  v-model="formCondicao.data"
                  label="Data *"
                  type="date"
                  density="compact"
                  variant="outlined" />
              </VCol>
            </VRow>
            <VAlert
              v-if="formErro"
              type="error"
              density="compact"
              class="mt-3"
              >{{ formErro }}</VAlert
            >
            <div class="d-flex justify-end gap-2 mt-4">
              <VBtn variant="outlined" @click="dialogCondicao = false"
                >Cancelar</VBtn
              >
              <VBtn type="submit" color="primary" :loading="enviando"
                >Salvar</VBtn
              >
            </div>
          </VForm>
        </VCardText>
      </VCard>
    </VDialog>

    <!-- Dialog Jornada -->
    <VDialog v-model="dialogJornada" max-width="700" persistent>
      <VCard>
        <VCardTitle>
          <VIcon icon="tabler-clock" class="me-2" />
          {{ formJornada.id ? "Editar" : "Nova" }} Jornada de Trabalho
        </VCardTitle>
        <VDivider />
        <VCardText>
          <VForm @submit.prevent="salvarJornada">
            <VRow>
              <VCol cols="12" md="6">
                <VAutocomplete
                  v-model="formJornada.funcionario_id"
                  :items="funcionariosOptions"
                  item-title="nome"
                  item-value="id"
                  label="Funcionário *"
                  density="compact"
                  variant="outlined"
                  @update:model-value="onFuncionarioChange" />
              </VCol>
              <VCol cols="12" md="6">
                <VTextField
                  v-model="formJornada.funcao"
                  label="Função"
                  density="compact"
                  variant="outlined"
                  readonly />
              </VCol>
              <VCol cols="12" md="4">
                <VTextField
                  v-model="formJornada.data"
                  label="Data *"
                  type="date"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="6" md="2">
                <VTextField
                  v-model="formJornada.entrada_manha"
                  label="Ent. Manhã"
                  type="time"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="6" md="2">
                <VTextField
                  v-model="formJornada.saida_manha"
                  label="Saída Manhã"
                  type="time"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="6" md="2">
                <VTextField
                  v-model="formJornada.entrada_tarde"
                  label="Ent. Tarde"
                  type="time"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="6" md="2">
                <VTextField
                  v-model="formJornada.saida_tarde"
                  label="Saída Tarde"
                  type="time"
                  density="compact"
                  variant="outlined" />
              </VCol>
            </VRow>
            <VAlert
              v-if="formErro"
              type="error"
              density="compact"
              class="mt-3"
              >{{ formErro }}</VAlert
            >
            <div class="d-flex justify-end gap-2 mt-4">
              <VBtn variant="outlined" @click="dialogJornada = false"
                >Cancelar</VBtn
              >
              <VBtn type="submit" color="primary" :loading="enviando"
                >Salvar</VBtn
              >
            </div>
          </VForm>
        </VCardText>
      </VCard>
    </VDialog>

    <!-- Dialog Serviço -->
    <VDialog v-model="dialogServico" max-width="600" persistent>
      <VCard>
        <VCardTitle>
          <VIcon icon="tabler-hammer" class="me-2" />
          {{ formServico.id ? "Editar" : "Novo" }} Serviço
        </VCardTitle>
        <VDivider />
        <VCardText>
          <VForm @submit.prevent="salvarServico">
            <VRow>
              <VCol cols="12" md="6">
                <VTextField
                  v-model="formServico.data_inicio"
                  label="Data Início"
                  type="date"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="12" md="6">
                <VTextField
                  v-model="formServico.data_fim"
                  label="Data Término"
                  type="date"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="12">
                <VTextField
                  v-model="formServico.frente_trabalho"
                  label="Frente de Trabalho *"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="12">
                <VTextarea
                  v-model="formServico.descricao"
                  label="Descrição do Serviço *"
                  density="compact"
                  variant="outlined"
                  rows="3" />
              </VCol>
              <VCol cols="12">
                <VAutocomplete
                  v-model="formServico.funcionarios_ids"
                  :items="funcionariosDoDia"
                  item-title="nome"
                  item-value="id"
                  label="Efetivo a realizar o serviço"
                  density="compact"
                  variant="outlined"
                  multiple
                  chips
                  closable-chips />
                <p class="text-caption text-medium-emphasis mt-1">
                  Selecione os funcionários que estão trabalhando nesta data.
                </p>
              </VCol>
              <VCol v-if="formServico.id" cols="12">
                <VSwitch
                  v-model="formServico.somente_este_dia"
                  label="Aplicar alteração apenas para este dia"
                  color="primary"
                  hide-details />
                <p class="text-caption text-medium-emphasis mt-1">
                  Se desmarcado, a alteração afetará todos os dias em que esta frente de trabalho aparece.
                </p>
              </VCol>
            </VRow>
            <VAlert
              v-if="formErro"
              type="error"
              density="compact"
              class="mt-3"
              >{{ formErro }}</VAlert
            >
            <div class="d-flex justify-end gap-2 mt-4">
              <VBtn variant="outlined" @click="dialogServico = false"
                >Cancelar</VBtn
              >
              <VBtn type="submit" color="primary" :loading="enviando"
                >Salvar</VBtn
              >
            </div>
          </VForm>
        </VCardText>
      </VCard>
    </VDialog>

    <!-- Dialog Equipamento -->
    <VDialog v-model="dialogEquipamento" max-width="500" persistent>
      <VCard>
        <VCardTitle>
          <VIcon icon="tabler-bulldozer" class="me-2" />
          {{ formEquipamento.id ? "Editar" : "Novo" }} Equipamento
        </VCardTitle>
        <VDivider />
        <VCardText>
          <VForm @submit.prevent="salvarEquipamento">
            <VRow>
              <VCol cols="12" md="6">
                <VTextField
                  v-model="formEquipamento.data_inicio"
                  label="Data Início"
                  type="date"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="12" md="6">
                <VTextField
                  v-model="formEquipamento.data_fim"
                  label="Data Término"
                  type="date"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="12">
                <VAutocomplete
                  v-model="formEquipamento.maquina_id"
                  :items="maquinasOptions"
                  item-title="nome"
                  item-value="id"
                  label="Máquina *"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="12" md="6">
                <VTextField
                  v-model.number="formEquipamento.quantidade"
                  label="Quantidade *"
                  type="number"
                  min="1"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="6" md="6">
                <VAutocomplete
                  v-model="formEquipamento.operador_id"
                  :items="funcionariosDoDia"
                  item-title="nome"
                  item-value="id"
                  label="Operador *"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol v-if="formEquipamento.id" cols="12">
                <VSwitch
                  v-model="formEquipamento.somente_este_dia"
                  label="Aplicar alteração apenas para este dia"
                  color="primary"
                  hide-details />
              </VCol>
            </VRow>
            <VAlert
              v-if="formErro"
              type="error"
              density="compact"
              class="mt-3"
              >{{ formErro }}</VAlert
            >
            <div class="d-flex justify-end gap-2 mt-4">
              <VBtn variant="outlined" @click="dialogEquipamento = false"
                >Cancelar</VBtn
              >
              <VBtn type="submit" color="primary" :loading="enviando"
                >Salvar</VBtn
              >
            </div>
          </VForm>
        </VCardText>
      </VCard>
    </VDialog>

    <!-- Dialog Ocorrência -->
    <VDialog v-model="dialogOcorrencia" max-width="500" persistent>
      <VCard>
        <VCardTitle>
          <VIcon icon="tabler-alert-triangle" class="me-2" />
          {{ formOcorrencia.id ? "Editar" : "Nova" }} Ocorrência
        </VCardTitle>
        <VDivider />
        <VCardText>
          <VForm @submit.prevent="salvarOcorrencia">
            <VRow>
              <VCol cols="12">
                <VTextField
                  v-model="formOcorrencia.data"
                  label="Data *"
                  type="date"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="12">
                <VTextarea
                  v-model="formOcorrencia.descricao"
                  label="Descrição da Ocorrência *"
                  density="compact"
                  variant="outlined"
                  rows="4" />
              </VCol>
            </VRow>
            <VAlert
              v-if="formErro"
              type="error"
              density="compact"
              class="mt-3"
              >{{ formErro }}</VAlert
            >
            <div class="d-flex justify-end gap-2 mt-4">
              <VBtn variant="outlined" @click="dialogOcorrencia = false"
                >Cancelar</VBtn
              >
              <VBtn type="submit" color="primary" :loading="enviando"
                >Salvar</VBtn
              >
            </div>
          </VForm>
        </VCardText>
      </VCard>
    </VDialog>

    <!-- Dialog Assinatura com Canvas -->
    <VDialog v-model="dialogAssinatura" max-width="550" persistent>
      <VCard>
        <VCardTitle class="d-flex align-center">
          <VIcon icon="tabler-signature" class="me-2" />
          Assinatura - {{ tipoAssinaturaLabel }}
        </VCardTitle>
        <VDivider />
        <VCardText>
          <p class="text-body-2 text-medium-emphasis mb-3">
            Desenhe sua assinatura no campo abaixo usando o mouse ou toque.
          </p>
          <div class="canvas-container">
            <canvas
              ref="canvasAssinatura"
              width="500"
              height="200"
              class="canvas-assinatura"
              @mousedown="iniciarDesenho"
              @mousemove="desenhar"
              @mouseup="pararDesenho"
              @mouseleave="pararDesenho"
              @touchstart.prevent="iniciarDesenhoTouch"
              @touchmove.prevent="desenharTouch"
              @touchend="pararDesenho" />
          </div>
          <div class="d-flex justify-center gap-2 mt-3">
            <VBtn
              size="small"
              variant="outlined"
              color="secondary"
              @click="limparCanvas">
              <VIcon icon="tabler-eraser" start />
              Limpar
            </VBtn>
          </div>
          <VAlert v-if="formErro" type="error" density="compact" class="mt-3">{{
            formErro
          }}</VAlert>
          <div class="d-flex justify-end gap-2 mt-4">
            <VBtn variant="outlined" @click="fecharDialogAssinatura"
              >Cancelar</VBtn
            >
            <VBtn color="primary" :loading="enviando" @click="salvarAssinatura"
              >Salvar Assinatura</VBtn
            >
          </div>
        </VCardText>
      </VCard>
    </VDialog>

    <!-- Dialog Editar Projeto -->
    <VDialog v-model="dialogEditarProjeto" max-width="600" persistent>
      <VCard>
        <VCardTitle>
          <VIcon icon="tabler-edit" class="me-2" />
          Editar Dados Contratuais
        </VCardTitle>
        <VDivider />
        <VCardText>
          <VForm @submit.prevent="salvarProjeto">
            <VRow>
              <VCol cols="12" md="6">
                <VTextField
                  v-model="formProjeto.numero_projeto"
                  label="Número do Projeto"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="12" md="6">
                <VTextField
                  v-model="formProjeto.nome_projeto"
                  label="Nome do Projeto"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="12">
                <VAutocomplete
                  v-model="formProjeto.cliente_id"
                  :items="clientesOptions"
                  item-title="nome"
                  item-value="id"
                  label="Cliente"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="12" md="6">
                <VTextField
                  v-model="formProjeto.data_inicial"
                  label="Data Inicial"
                  type="date"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="12" md="6">
                <VTextField
                  v-model="formProjeto.data_final"
                  label="Data Final"
                  type="date"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="12">
                <VTextField
                  v-model="formProjeto.local_cidade"
                  label="Local / Cidade"
                  density="compact"
                  variant="outlined" />
              </VCol>
              <VCol cols="12">
                <VTextarea
                  v-model="formProjeto.dados_contratuais"
                  label="Dados Contratuais"
                  density="compact"
                  variant="outlined"
                  rows="3" />
              </VCol>
            </VRow>
            <VAlert
              v-if="formErro"
              type="error"
              density="compact"
              class="mt-3"
              >{{ formErro }}</VAlert
            >
            <div class="d-flex justify-end gap-2 mt-4">
              <VBtn variant="outlined" @click="dialogEditarProjeto = false"
                >Cancelar</VBtn
              >
              <VBtn type="submit" color="primary" :loading="enviando"
                >Salvar</VBtn
              >
            </div>
          </VForm>
        </VCardText>
      </VCard>
    </VDialog>

    <!-- Modal confirmar -->
    <VDialog v-model="dialogConfirmar" max-width="450" persistent>
      <VCard>
        <VCardTitle class="text-h6">
          <VIcon icon="tabler-help-circle" class="me-2" color="warning" />
          Confirmar Ação
        </VCardTitle>
        <VCardText>
          {{ confirmarMsg }}
          <template v-if="dialogConfirmarExcluirRDO">
            <p class="mt-3 text-body-2">Como deseja proceder com esta exclusão?</p>
          </template>
        </VCardText>
        <VCardActions class="pa-4">
          <VSpacer />
          <VBtn variant="text" @click="fecharConfirmar">Cancelar</VBtn>
          
          <template v-if="dialogConfirmarExcluirRDO">
            <VBtn color="warning" variant="tonal" @click="executarConfirmar(true)">Somente este dia</VBtn>
            <VBtn color="error" @click="executarConfirmar(false)">Todos os dias</VBtn>
          </template>
          <template v-else>
            <VBtn color="error" @click="executarConfirmar(false)">Confirmar</VBtn>
          </template>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Modal aviso -->
    <VDialog v-model="dialogAviso" max-width="400" persistent>
      <VCard>
        <VCardTitle class="d-flex align-center">
          <VIcon icon="tabler-alert-circle" color="error" class="me-2" />
          Aviso
          <VSpacer />
          <VBtn icon variant="text" size="small" @click="fecharAviso">
            <VIcon icon="tabler-x" />
          </VBtn>
        </VCardTitle>
        <VCardText>{{ avisoMsg }}</VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn color="primary" variant="tonal" @click="fecharAviso">OK</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<style scoped>
.canvas-container {
  border: 2px solid #ccc;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.canvas-assinatura {
  display: block;
  width: 100%;
  height: 200px;
  cursor: crosshair;
  touch-action: none;
}

.assinatura-preview {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px;
  background: #fafafa;
}

.assinatura-img {
  max-width: 100%;
  height: auto;
  max-height: 80px;
  display: block;
  margin: 0 auto;
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(12px);
  background-color: rgba(var(--v-theme-surface), 0.8) !important;
  border-bottom: 2px solid rgb(var(--v-theme-primary), 0.1);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  margin-top: -1.5rem !important;
  margin-inline: -1rem;
  padding-inline: 1rem;
  border-radius: 0 0 12px 12px !important;
}

.text-primary {
  color: rgb(var(--v-theme-primary)) !important;
}

.line-height-1 {
  line-height: 1 !important;
}

.gap-4 {
  gap: 1rem;
}
</style>
